import { useEffect, useState } from "react";
import { Calendar, CircleChevronLeft } from "lucide-react";
import toast from "react-hot-toast";
import { InverntroyService } from "../services/api/inventroy.service";

const formStyles = {
  lableStyle: "block text-xs text-[#3F5260] font-medium mb-1",
  inputStyle:
    "w-full px-3 py-2 border border-[#D0D5DE] outline-none rounded-lg focus:ring-1 focus:ring-emerald-500 text-xs",
};

interface Props {
  onClose: () => void;
}

const UpdateSellingPriceModal: React.FC<Props> = ({
  id,
  productID,
  onClose,
}: {
  productID: string;
  id: string;
  onClose: () => void;
}) => {
  // Loading state
  console.log(productID, id);
  const [isLoading, setIsLoading] = useState(false);

  // Existing state
  const [categoryName, setCategoryName] = useState("");
  const [productImagePreviews, setProductImagePreviews] = useState({
    main: "",
    sub: ["", "", ""],
  });

  // Product Information States (Read-only)
  const [productName, setProductName] = useState("");
  const [isRxRequired, setIsRxRequired] = useState(false);
  const [productDescription, setProductDescription] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [batchNumber, setBatchNumber] = useState("");

  // Current Prices States (Read-only)
  const [currentMrp, setCurrentMrp] = useState<number>(0);
  const [currentProductRate, setCurrentProductRate] = useState<number>(0);
  const [currentLandingCost, setCurrentLandingCost] = useState<number>(0);
  const [mrpLessGst, setMrpLessGst] = useState<number>(0);

  // Update Prices States (Editable)
  const [updateMrp, setUpdateMrp] = useState<number>(0);
  const [sellingDiscount, setSellingDiscount] = useState<number>(0);
  const [currentDiscountPercentage, setCurrentDiscountPercentage] =
    useState<number>(0);
  const [finalSellingPrice, setFinalSellingPrice] = useState<number>(0);
  const [categoryId, setCategoryId] = useState("");

  const loadInwardProductDetails = async () => {
    try {
      const response = await InverntroyService.getInWardProductDetails(id);

      if (response?.data) {
        const data = response.data;

        // Set Product Information (Read-only fields)
        setProductName(data.product?.name || "");
        setIsRxRequired(data.product?.is_rx_required || false);
        setProductDescription(data.product?.description || "");
        setCategoryId(data.product?.category_id || "");
        setManufacturer(data.manufacturer?.manufacturer_name || "");
        setCategoryName(data.product?.categoryName || "");

        

        // Format dates
        if (data.expiry_date) {
          const date = new Date(data.expiry_date);
          const formattedDate = date.toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
          });
          setExpiryDate(formattedDate);
        }

        setBatchNumber(data.batch_number || "");

        // Set product images
        if (data.productMedia && data.productMedia.length > 0) {
          const mainImage = data.productMedia.find((media) => media.isMain);
          const subImages = data.productMedia.filter((media) => !media.isMain);

          if (mainImage) {
            setProductImagePreviews((prev) => ({
              ...prev,
              main: mainImage.product_image_path,
            }));
          }

          if (subImages.length > 0) {
            const subImageUrls = ["", "", ""];
            subImages.forEach((img, index) => {
              if (index < 3) {
                subImageUrls[index] = img.product_image_path;
              }
            });
            setProductImagePreviews((prev) => ({
              ...prev,
              sub: subImageUrls,
            }));
          }
        }

        // if(data?.productPrice){
        //   setFinalSellingPrice(data.productPrice.final_selling_price)
        //   setSellingDiscount(data.productPrice.selling_discount)
        // }

        // Set Current Prices (Read-only fields)
        setCurrentMrp(Number(data.mrp) || 0);
        setCurrentProductRate(Number(data.product_rate) || 0);
        setCurrentLandingCost(Number(data.landing_cost) || 0);
        const mrpwithoutGst = Number(data.mrp) / (1 + (Number(data.newCgst) + Number(data.newSgst)) / 100);
        setMrpLessGst(mrpwithoutGst);

        // Initialize Update Prices - MRP is prefilled from current MRP and not editable
        setUpdateMrp(Number(data.mrp) || 0);
        const discountPerc =
          ((Number(mrpwithoutGst) - Number(data.landing_cost)) / Number(mrpwithoutGst)) *
          100;
        setCurrentDiscountPercentage(discountPerc || 0);

        // Calculate final selling price based on MRP and discount
        if (data.mrp && data.discount) {
          const mrp = Number(data.mrp);
          if (!data?.productPrice) {
            setFinalSellingPrice(mrp);
          }
        }

        if(data.is_sp_updated){
          setFinalSellingPrice(data.productPrice.final_selling_price);
          setSellingDiscount(data.productPrice.selling_discount);
        }
      }
    } catch (error) {
      toast.error(error);
    }
  };

  const handleDiscountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Allow: empty, numbers, decimals (up to 2 decimal places)
    if (value === "" || /^(\d+(\.\d{0,2})?)?$/.test(value)) {
      let discount=value === "" ? 0 : parseFloat(value);
      if(discount>currentDiscountPercentage){
        toast.error("Discount cannot be greater than discount to dandar");
        return;
      }
      setSellingDiscount(discount);
    }
  };

  const handleUpdateSellingPrice = async () => {
    try {
      // Validation
      if (
        updateMrp <= 0 ||
        sellingDiscount === null 
        
      ) {
        toast.error("Please fill all required fields");
        return;
      }

      if (finalSellingPrice < currentLandingCost || finalSellingPrice <= 0) {
        toast.error("Selling price less than landing cost to Dandar");
        return;
      }

      setIsLoading(true);

      // Prepare data for API call
      const updateData = {
        productId: productID,
        categoryId: categoryId,
        productMrp: parseFloat(updateMrp),
        inwardStockId: id,
        sellingDiscount: parseFloat(sellingDiscount),
        finalSellingPrice: parseFloat(finalSellingPrice),
        newMrp: parseFloat(updateMrp),
        newSp: parseFloat(finalSellingPrice),
      };

      const response = await InverntroyService.updateProductSellingPrice(
        updateData
      );

      if (response?.success || response?.status === 200) {
        toast.success("Product selling price updated successfully!");
        onClose(); // Close modal on success
      } else {
        toast.error("Failed to update selling price");
      }
    } catch (error) {
      console.error("Error updating selling price:", error);
      toast.error(
        error?.message || "An error occurred while updating selling price"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-calculate final selling price when MRP or discount changes
  useEffect(() => {
    if (updateMrp && sellingDiscount !== null) {
      const finalPrice = updateMrp - (updateMrp * sellingDiscount) / 100;
      setFinalSellingPrice(finalPrice);
    }
  }, [updateMrp, sellingDiscount]);

  useEffect(() => {
    loadInwardProductDetails();
  }, []);

  return (
    <>
      <div className="flex items-center mb-4 mt-6">
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700"
          disabled={isLoading}
        >
          <CircleChevronLeft className="w-6 h-6 bg-[#808080] text-white rounded-full mr-2" />
        </button>
        <h2 className="font-medium text-sm">Update Selling Price</h2>
      </div>
      <div className="w-full px-4 sm:px-6 py-6 bg-white">
        {/* Header */}
        <div className="flex items-center gap-2 mb-6">
          <h2 className="text-sm font-semibold text-green-500">
            PRODUCT INFORMATION
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Product Name */}
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2 gap-2">
                <label className="text-xs font-medium text-gray-700">
                  Product Name
                </label>
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <span
                    className={`w-3 h-3 rounded-full border ${
                      isRxRequired
                        ? "bg-green-500 border-green-500"
                        : "border-gray-300"
                    }`}
                  ></span>
                  Rx Required?
                </span>
              </div>
              <input
                type="text"
                value={productName}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Product Description
              </label>
              <textarea
                rows={4}
                value={productDescription}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none bg-gray-50"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Category
              </label>
              <input
                type="text"
                value={categoryName}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
              />
            </div>

            {/* Manufacturer and Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 ">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Manufacturer
                </label>
                <input
                  type="text"
                  value={manufacturer}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Expiry Date
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={expiryDate}
                    readOnly
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                  />
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Batch Number
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={batchNumber}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                  />
                </div>
              </div>
            </div>

            {/* Current Prices */}
            <div className="rounded-lg space-y-4">
              <h3 className="text-sm font-semibold text-green-500 mb-4">
                CURRENT PRICES
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Product M.R.P
                  </label>
                  <input
                    type="number"
                    value={Number(currentMrp).toFixed(2)}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Product M.R.P without GST
                  </label>
                  <input
                    type="number"
                    value={Number(mrpLessGst).toFixed(2)}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Product Rate
                  </label>
                  <input
                    type="number"
                    value={Number(currentProductRate).toFixed(2)}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Landing cost to Dandar
                  </label>
                  <input
                    type="number"
                    value={Number(currentLandingCost).toFixed(2)}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Discount % to Dandar
                  </label>
                  <input
                    type="number"
                    value={Number(currentDiscountPercentage).toFixed(2)}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Images */}
          <div className="space-y-8">
            {/* Main Photo */}
            <section className="  h-full">
              <div>
                <label
                  htmlFor="main-product-image"
                  className={formStyles.lableStyle}
                >
                  Product Main Photo
                </label>
                <div className="bg-[#f1f3f3] w-full h-48 md:h-64 lg:h-[279px] rounded-lg flex justify-center items-center overflow-hidden">
                  {productImagePreviews.main && (
                    <div className="relative w-full h-full">
                      <img
                        src={productImagePreviews.main}
                        alt="Main product"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className=" my-4">
                <label className={formStyles.lableStyle}>
                  Sub Product Photos
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[0, 1, 2].map((index) => (
                    <div
                      key={index}
                      className="bg-[#f1f3f3] w-full h-32 md:h-[120px] rounded-lg flex justify-center items-center overflow-hidden"
                    >
                      {productImagePreviews.sub[index] && (
                        <div className="relative w-full h-full">
                          <img
                            src={productImagePreviews.sub[index]}
                            alt={`Sub product ${index + 1}`}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>

          {/* Update Prices */}
          <div className=" rounded-lg space-y-4">
            <h3 className="text-sm font-semibold text-green-500 mb-4">
              UPDATE PRICES
            </h3>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              Product M.R.P
            </label>
            <input
              type="number"
              value={updateMrp.toFixed(2)}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 cursor-not-allowed"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              Selling Discount %
            </label>
            <input
              type="text"
              inputMode="decimal"
              disabled={isLoading}
              value={sellingDiscount.toString()}
              onChange={handleDiscountChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              Final Selling Price
            </label>
            <input
              type="number"
              value={Number(finalSellingPrice).toFixed(2)}
              onChange={(e) => setFinalSellingPrice(parseFloat(e.target.value) || 0)}
              disabled={isLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 my-4">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-bold disabled:bg-red-400 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdateSellingPrice}
            disabled={isLoading}
            className="px-4 py-2 bg-green-500 text-white rounded-md text-sm font-bold disabled:bg-green-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Updating...
              </>
            ) : (
              "Update Price"
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default UpdateSellingPriceModal;
